import { cn, formatCurrency, timeAgo } from '@/lib/utils';
import { Flex } from '@radix-ui/themes';
import React from 'react';

import crown from "@/public/crown-solid.svg";
import lightning from "@/public/lightning.svg";
import Image from 'next/image';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/app/components/ui/tooltip';
import { NoncedBid } from './util';

interface Props {
  bid: NoncedBid;
  currentUserId: string | undefined;
}

const BidHistoryRow = ({ bid, currentUserId }: Props) => {
  const {
    nonceId,
    createdAt,
    amount,
    currentHighest = false,
    removed,
    userId: bidUserId,
    isAuto
  } = bid;
  const time = new Date(createdAt);
  const bidderLabel = (() => {
    const floorMatch = bidUserId.match(/__floorbidder(\d+)__/);
    if (floorMatch) {
      return `Floor Bidder`;
    }
    if (currentUserId === bidUserId) {
      return `Bidder ${nonceId} (You)`;
    }
    return `Bidder ${nonceId}`;
  })();

  return (
    <Flex
      className={cn(
        "w-full shrink-0 py-1.5 rounded-md h-[36px] text-[9pt] sm:text-[12pt]",
        currentHighest && "font-semibold",
        removed && "relative",
      )}
      align="center"
      gap="2"
    >
      {removed && <Strikethrough />}
      <Flex className="w-[120px] sm:w-[142px] text-zinc-700 select-none mr-3 text-[9pt] sm:text-[12pt]" align="center">
        {bidderLabel}
      </Flex>
      <Flex className={cn(
        "text-zinc-500 flex-grow text-[9pt] sm:text-[12pt]",
      )}>
        {timeAgo(time)}
      </Flex>
      <Flex align="center" className={cn(
        "text-zinc-700 text-[9pt] sm:text-[12pt]",
      )}>
        <Flex align="center" gap="1" className="mr-1">
          {isAuto &&
            <AutoBidIndicator />
          }
          {currentHighest &&
            <HighestBidIndicator />
          }
        </Flex>
        {formatCurrency(amount)}
      </Flex>
    </Flex >
  );
}

export const Strikethrough = () => (
  <Tooltip delayDuration={150}>
    <TooltipTrigger asChild>
      <Flex className="hover:bg-zinc-300/30 h-5 absolute w-full" align="center" >
        <Flex className="bg-zinc-500 h-[1px] absolute w-full " />
      </Flex>
    </TooltipTrigger>
    <TooltipContent>
      Removed
    </TooltipContent>
  </Tooltip>
)

const HighestBidIndicator = () => (
  <Tooltip delayDuration={150}>
    <TooltipTrigger asChild>
      <Image src={crown} alt="Crown" width={14} height={14} className="h-3 w-3 sm:h-[14px] sm:w-[14px]" />
    </TooltipTrigger>
    <TooltipContent className="font-light">
      Highest bid
    </TooltipContent>
  </Tooltip>
)

const AutoBidIndicator = () => (
  <Tooltip delayDuration={150}>
    <TooltipTrigger asChild>
      <Image src={lightning} alt="Lightning bolt" width={14} height={14} className="h-3 w-3 sm:h-[14px] sm:w-[14px]" />
    </TooltipTrigger>
    <TooltipContent className="font-light">
      Automatic bid
    </TooltipContent>
  </Tooltip>
)

export default BidHistoryRow
